import {
  Children,
  Component,
  ComponentProps,
  FocusEvent,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { GenericFieldHTMLAttributes, WrappedFieldProps } from 'redux-form';
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from 'shared/components/GooglePlacesAutocomplete';
import { COLORS, HEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { CountryCode } from 'lib/constants/locales';
import FormControlProps from 'lib/interfaces/FormControlProps';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { captureMessage } from 'lib/reporting';
import ThemedFormInput from 'verify/components/ThemedFormInput';
import { Z_INDICES } from 'verify/styles/variables';

const DropdownWrapper = styled('div')`
  position: relative;

  // Move spacing up from themed form input to full component
  margin-bottom: ${SPACINGS.base * 2}px;
  & > div {
    margin-bottom: 0;
  }
`;

const DropdownContainer = styled('div')<{ openUpwards: boolean }>`
  background-color: ${({ theme }) => theme.colorDropdownBackground};
  border: 1px solid ${COLORS.gray20};
  position: absolute;
  z-index: ${Z_INDICES.dropdown};
  width: 100%;

  ${({ openUpwards }) => openUpwards && `bottom: ${HEIGHTS.input}`}
`;

enum ComponentType {
  StreetNumber = 'street_number',
  Subpremise = 'subpremise',

  Route = 'route',

  City = 'locality',
  /**
   * Sublocality is relevant for the NYC boroughs.
   * Google does not return the city as New York / New York City, but instead encodes the borough (Brooklyn / Manhattan
   * / etc.) as `sublocality_level_1` and the county as `administrative_area_level_2`. The sublocality is generally
   * more specific and clear than the county if present.
   */
  Sublocality = 'sublocality_level_1',
  County = 'administrative_area_level_2',
  Neighborhood = 'neighborhood',

  PostalCode = 'postal_code',
  State = 'administrative_area_level_1',
}

interface DropdownItemProps {
  active?: boolean;
}

const DropdownItem = styled('div')<DropdownItemProps>`
  cursor: pointer;
  padding: ${SPACINGS.base}px;

  &:hover,
  &:focused {
    background-color: ${({ theme }) => theme.colorDropdownOption};
  }

  ${(props) =>
    props.active &&
    css`
      background-color: ${({ theme }) => theme.colorDropdownOption};
    `};
`;

interface LocationSearchInputProps extends FormControlProps {
  boundChangeFormValue: (field: string, value: string) => void;
  onChange: (value: string) => void;
  value: string;

  selectedCountryCode: CountryCode;
  latLng?: google.maps.LatLngLiteral;

  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
}

interface LocationSearchInputState {
  isFocused: boolean;
}

export default class LocationSearchInput extends Component<
  LocationSearchInputProps & WrappedFieldProps & GenericFieldHTMLAttributes,
  LocationSearchInputState
> {
  state = {
    isFocused: false,
  };

  public render() {
    const { selectedCountryCode, latLng } = this.props;
    let searchOptions;
    // window.google.maps is empty hash on old versions of safari, so do a check for the
    // actual constructor we want to avoid undefined errors
    // note that autocomplete still won't work but we should just autoexpand in the parent
    // component when we detect window.google.maps.places is undefined
    if (latLng && window.google?.maps?.LatLng) {
      searchOptions = {
        componentRestrictions: { country: selectedCountryCode },
        // Despite type definition, Google Places API does NOT take a LatLngLiteral
        location: new window.google.maps.LatLng(latLng),
        radius: 4000,
      };
    } else {
      searchOptions = {
        componentRestrictions: { country: selectedCountryCode },
      };
    }

    return (
      <GooglePlacesAutocomplete
        onChange={this.handleChange}
        // Errors are silenced
        onError={this.handleAutocompleteError}
        value={this.props.input.value}
        onSelect={this.handleSelect}
        searchOptions={searchOptions}
        // See: verify.html.erb script tag. Needed for async defer for speed.
        googleCallbackName='onGoogleMapsLoaded'
      >
        {this.renderSuggestions}
      </GooglePlacesAutocomplete>
    );
  }

  private renderSuggestions: ComponentProps<typeof GooglePlacesAutocomplete>['children'] = ({
    getInputProps,
    suggestions,
    getSuggestionItemProps,
    loading,
  }) => {
    const { onBlur, onChange, onFocus, input, ...inputProps } = this.props;
    const { isFocused } = this.state;

    const toRenderSuggestions = isFocused ? suggestions : [];
    const renderedSuggestions = toRenderSuggestions.map((suggestion) => (
      <DropdownItem
        {...getSuggestionItemProps(suggestion, {
          key: suggestion.description,
          active: suggestion.active,
        })}
        key={suggestion.placeId}
      >
        {suggestion.description}
      </DropdownItem>
    ));

    return (
      <DropdownWrapper>
        <ThemedFormInput
          id={input.name}
          input={input}
          {...getInputProps({
            name: input.name,
            placeholder: 'Address',

            onBlur: this.handleInputBlur,
            onFocus: this.handleInputFocus,
          })}
          {...inputProps}
        />
        <Locations loading={loading}>{renderedSuggestions}</Locations>
      </DropdownWrapper>
    );
  };

  private handleAutocompleteError = (status: google.maps.places.PlacesServiceStatus) => {
    // Ignore transient errors
    if (
      ![
        google.maps.places.PlacesServiceStatus.ZERO_RESULTS,
        // Transient errors according to Google
        google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR,
        // Google maps errors when there's a burst of requests.
        // There is no configurable quota for this.
        google.maps.places.PlacesServiceStatus.REQUEST_DENIED,
        google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT,
      ].includes(status)
    ) {
      captureMessage('Google Maps API returned error with status: ' + status);
    }
  };

  private handleChange = (address: string | null | undefined) => {
    // Only change street address
    this.props.input.onChange(address);
  };

  private handleSelect = async (_: unknown, placeId?: string | null) => {
    if (!placeId) {
      // No autocomplete option was selected
      return;
    }

    // Selected full autocomplete option
    let geocode: google.maps.GeocoderResult[];
    try {
      geocode = await geocodeByPlaceId(placeId);
    } catch (status) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.handleAutocompleteError(status);
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const components = geocode[0].address_components;
    const getAddressComponent = (name: string, short = false) => {
      const matches = components.filter((c) => c.types.indexOf(name) !== -1);
      if (matches.length > 0) {
        return short ? matches[0].short_name : matches[0].long_name;
      } else {
        return '';
      }
    };

    // Update Street 1 and maybe Street 2 based on best match
    // If StreetNumber is present, use it for street 1 and Subpremise for street 2
    // Otherwise, use Subpremise for street 1 and leave street 2 blank
    const streetNumber = getAddressComponent(ComponentType.StreetNumber);
    if (streetNumber) {
      this.props.boundChangeFormValue(
        PiiAttributes.AddressStreet1,
        [streetNumber, getAddressComponent(ComponentType.Route)].filter((c) => c).join(' '),
      );
      this.props.boundChangeFormValue(
        PiiAttributes.AddressStreet2,
        getAddressComponent(ComponentType.Subpremise),
      );
    } else {
      this.props.boundChangeFormValue(
        PiiAttributes.AddressStreet1,
        [getAddressComponent(ComponentType.Subpremise), getAddressComponent(ComponentType.Route)]
          .filter((c) => c)
          .join(' '),
      );
    }

    // Update other fields
    this.props.boundChangeFormValue(
      PiiAttributes.AddressCity,
      getAddressComponent(ComponentType.City) ||
        getAddressComponent(ComponentType.Sublocality) ||
        getAddressComponent(ComponentType.County) ||
        getAddressComponent(ComponentType.Neighborhood),
    );
    this.props.boundChangeFormValue(
      PiiAttributes.AddressSubdivision,
      getAddressComponent(ComponentType.State),
    );
    this.props.boundChangeFormValue(
      PiiAttributes.AddressPostalCode,
      getAddressComponent(ComponentType.PostalCode),
    );
  };

  private handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    this.setState({
      isFocused: false,
    });

    if (this.props.input.onBlur) {
      this.props.input.onBlur(event);
    }
  };

  private handleInputFocus = (event: FocusEvent<HTMLTextAreaElement>) => {
    this.setState({
      isFocused: true,
    });

    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };
}

const Locations = ({ loading, children }: { loading: boolean; children: ReactNode }) => {
  const [openUpwards, setOpenUpwards] = useState(false);
  const containerRef = useRef<HTMLDivElement>();
  const rowCount = Children.count(children);

  useLayoutEffect(() => {
    // offsetParent can be null: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    if (containerRef.current == null || containerRef.current.offsetParent == null) {
      return;
    }

    const bottomOfSearchInput = containerRef.current.offsetParent.getBoundingClientRect().bottom;
    const bottomOfSearchMenu =
      bottomOfSearchInput + containerRef.current.getBoundingClientRect().height;

    // Open downwards only if there is enough space in the widget; otherwise, open up
    setOpenUpwards(document.documentElement.clientHeight < bottomOfSearchMenu);
  }, [rowCount]);

  if (rowCount === 0) {
    return null;
  }

  return (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <DropdownContainer ref={containerRef} openUpwards={openUpwards}>
      {loading && <DropdownItem active={false}>Loading...</DropdownItem>}
      {children}
    </DropdownContainer>
  );
};
